import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { PlannerServicePlan, useChurchPlanner, usePlannerRoles } from '../../hooks/useChurchPlanner';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { useChurchPlannerRail } from './ChurchPlannerRailContext';
import {
  PLANNER_TEAM_DEFINITIONS,
  resolveTeamIdFromRole,
} from '../../hooks/usePlannerInsights';
import { hasAnyPermission, Permission } from '../../utils/roles';
import {
  PlannerPage,
  PlannerCard,
  PlannerSubtle,
  PlannerError,
  plannerColors,
} from './plannerTheme';
import { formatPlannerStatusLabel, normalizePlannerStatus } from './plannerStatus';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    gap: 0.45rem;
  }
`;

const ConnectedStack = styled.div`
  --planner-rail-offset: 1.15rem;
  --planner-rail-width: 2px;
  --planner-node-size: 10px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &::before {
    content: '';
    position: absolute;
    left: var(--planner-rail-offset);
    top: 1.1rem;
    bottom: 1.1rem;
    width: var(--planner-rail-width);
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(247, 204, 108, 0.55), rgba(141, 176, 255, 0.48));
    pointer-events: none;
    opacity: 0.82;
  }

  @media (max-width: 640px) {
    --planner-rail-offset: 1.3rem;
    --planner-node-size: 9px;
    gap: 0.72rem;

    &::before {
      top: 0.95rem;
      bottom: 0.95rem;
    }
  }
`;

const ConnectedCard = styled(PlannerCard)`
  position: relative;
  margin-left: calc(var(--planner-rail-offset) + 0.84rem);

  &::before {
    content: '';
    position: absolute;
    left: calc((var(--planner-rail-offset) * -1) - 0.08rem);
    top: 1.1rem;
    width: var(--planner-node-size);
    height: var(--planner-node-size);
    border-radius: 50%;
    background: #d9e3ff;
    border: 2px solid rgba(18, 18, 20, 0.95);
    box-shadow: 0 0 0 4px rgba(141, 176, 255, 0.18);
  }

  @media (max-width: 640px) {
    padding: 0.85rem 0.9rem;
    border-radius: 14px;

    &::before {
      top: 1rem;
    }
  }
`;

const HeroCard = styled(ConnectedCard)`
  background:
    radial-gradient(circle at 18% 12%, rgba(247, 204, 108, 0.4), transparent 42%),
    linear-gradient(155deg, #253049 0%, #1b253a 45%, #131a2a 100%);
  border-color: transparent;
  color: #f6f8ff;
  box-shadow: 0 20px 45px rgba(11, 16, 28, 0.34);
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid rgba(247, 204, 108, 0.85);
    outline-offset: 3px;
  }
`;

const CurrentServiceCard = styled(ConnectedCard)`
  background:
    radial-gradient(circle at 12% 12%, rgba(109, 230, 180, 0.22), transparent 42%),
    linear-gradient(155deg, #1f3a34 0%, #1a2d2b 45%, #131f1e 100%);
  border-color: rgba(109, 230, 180, 0.34);
  color: #f3fff8;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid rgba(109, 230, 180, 0.85);
    outline-offset: 3px;
  }
`;

const HeroLayout = styled.div`
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 1rem;

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 640px) {
    gap: 0.72rem;
  }
`;

const HeroImageWrap = styled.div`
  border-radius: 14px;
  overflow: hidden;
  min-height: 170px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.12);

  @media (max-width: 640px) {
    min-height: 136px;
    border-radius: 12px;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  min-height: 170px;
  object-fit: cover;
  display: block;

  @media (max-width: 640px) {
    min-height: 136px;
  }
`;

const HeroPill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.67rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.16);
  color: #f6f8ff;

  @media (max-width: 640px) {
    font-size: 0.62rem;
    padding: 0.22rem 0.58rem;
  }
`;

const HeroTitle = styled.h2`
  margin: 0.65rem 0 0.35rem;
  font-size: 1.35rem;
  line-height: 1.2;
  color: #f6f8ff;

  @media (max-width: 640px) {
    margin: 0.42rem 0 0.22rem;
    font-size: 1.08rem;
    line-height: 1.24;
  }
`;

const HeroMeta = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.9rem;
  color: rgba(246, 248, 255, 0.86);

  @media (max-width: 640px) {
    margin-top: 0.1rem;
    font-size: 0.79rem;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${plannerColors.text};

  @media (max-width: 640px) {
    font-size: 0.93rem;
  }
`;

const AccentLink = styled(Link)`
  color: #eef2ff;
  font-weight: 700;
  text-decoration: none;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border: 1px solid rgba(238, 242, 255, 0.28);
  border-radius: 999px;
  padding: 0.4rem 0.68rem;
  background: rgba(238, 242, 255, 0.09);
  transition: background 140ms ease, transform 140ms ease;

  &:hover {
    background: rgba(238, 242, 255, 0.18);
    transform: translateY(-1px);
  }

  @media (max-width: 640px) {
    font-size: 0.66rem;
    letter-spacing: 0.06em;
    padding: 0.32rem 0.54rem;
  }
`;

const EmptyUpcomingActions = styled.div`
  margin-left: calc(var(--planner-rail-offset) + 0.84rem);
  display: flex;

  @media (max-width: 640px) {
    margin-left: calc(var(--planner-rail-offset) + 0.72rem);
  }
`;

const AddServiceLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0.72rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(89, 130, 255, 0.34);
  background: rgba(89, 130, 255, 0.14);
  color: ${plannerColors.text};
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-decoration: none;
  text-transform: uppercase;
  transition: transform 140ms ease, border-color 140ms ease, background 140ms ease;

  &:hover {
    border-color: rgba(141, 176, 255, 0.72);
    background: rgba(89, 130, 255, 0.22);
    transform: translateY(-1px);
  }
`;

const MyAssignmentsCard = styled(ConnectedCard)`
  background:
    radial-gradient(circle at 92% 8%, rgba(229, 211, 179, 0.16), transparent 36%),
    linear-gradient(180deg, rgba(247, 248, 250, 0.05), rgba(247, 248, 250, 0.01)),
    ${plannerColors.surface};
  border-color: rgba(229, 211, 179, 0.34);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
`;

const ServiceHealthCard = styled(ConnectedCard)`
  background:
    radial-gradient(circle at 90% 0%, rgba(89, 130, 255, 0.14), transparent 36%),
    ${plannerColors.surface};
  border-color: rgba(89, 130, 255, 0.3);
`;

const AssignmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  @media (max-width: 640px) {
    gap: 0.5rem;
  }
`;

const AssignmentItem = styled.div`
  border: 1px solid rgba(229, 211, 179, 0.28);
  border-radius: 12px;
  padding: 0.65rem;
  background:
    linear-gradient(145deg, rgba(45, 42, 37, 0.36), rgba(31, 31, 33, 0.95)),
    linear-gradient(145deg, rgba(37, 37, 40, 0.92), rgba(31, 31, 33, 0.95));
  display: grid;
  grid-template-columns: 66px minmax(0, 1fr);
  gap: 0.72rem;
  align-items: center;

  @media (max-width: 640px) {
    padding: 0.5rem;
    border-radius: 10px;
    grid-template-columns: 54px minmax(0, 1fr);
    gap: 0.54rem;
  }
`;

const AssignmentImage = styled.img`
  width: 66px;
  height: 66px;
  border-radius: 11px;
  object-fit: cover;
  display: block;
  border: 1px solid rgba(229, 211, 179, 0.35);
  background: rgba(255, 255, 255, 0.06);

  @media (max-width: 640px) {
    width: 54px;
    height: 54px;
    border-radius: 9px;
  }
`;

const AssignmentBody = styled.div`
  min-width: 0;
`;

const AssignmentDate = styled.p`
  margin: 0;
  color: ${plannerColors.muted};
  font-size: 0.8rem;
  line-height: 1.35;

  @media (max-width: 640px) {
    font-size: 0.74rem;
  }
`;

const AssignmentRolePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.34rem;
  border-radius: 999px;
  padding: 0.22rem 0.62rem;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #211a10;
  background: linear-gradient(135deg, #f5d8a7 0%, #e7c286 100%);
  margin-right: 0.45rem;

  @media (max-width: 640px) {
    font-size: 0.62rem;
    padding: 0.18rem 0.5rem;
  }
`;

const AssignmentStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  margin-top: 0.45rem;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${plannerColors.accent};
  background: ${plannerColors.accentSoft};

  @media (max-width: 640px) {
    margin-top: 0.34rem;
    font-size: 0.61rem;
    padding: 0.16rem 0.42rem;
  }
`;

const AssignmentActions = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-top: 0.46rem;

  @media (max-width: 640px) {
    margin-top: 0.34rem;
    gap: 0.34rem;
  }
`;

const AssignmentActionButton = styled.button<{ $tone: 'ok' | 'bad'; $active?: boolean }>`
  border: 1px solid
    ${({ $tone, $active }) =>
      $active
        ? $tone === 'ok'
          ? 'rgba(52, 199, 89, 0.58)'
          : 'rgba(255, 69, 58, 0.58)'
        : plannerColors.border};
  border-radius: 999px;
  padding: 0.22rem 0.56rem;
  font-size: 0.63rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  color: ${({ $tone, $active }) =>
    $active
      ? $tone === 'ok'
        ? '#102113'
        : '#2c0f0e'
      : plannerColors.text};
  background: ${({ $tone, $active }) =>
    $active
      ? $tone === 'ok'
        ? 'rgba(52, 199, 89, 0.75)'
        : 'rgba(255, 69, 58, 0.76)'
      : plannerColors.surfaceHover};
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};

  &:hover {
    border-color: ${({ $tone }) => ($tone === 'ok' ? 'rgba(52, 199, 89, 0.52)' : 'rgba(255, 69, 58, 0.52)')};
  }
`;

const HealthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const HealthItem = styled.div`
  border: 1px solid rgba(89, 130, 255, 0.26);
  border-radius: 12px;
  padding: 0.75rem 0.8rem;
  background: rgba(89, 130, 255, 0.08);

  @media (max-width: 640px) {
    padding: 0.58rem 0.62rem;
    border-radius: 10px;
  }
`;

const HealthItemLink = styled(Link)`
  border: 1px solid rgba(89, 130, 255, 0.26);
  border-radius: 12px;
  padding: 0.75rem 0.8rem;
  background: rgba(89, 130, 255, 0.08);
  text-decoration: none;
  transition: border-color 140ms ease, transform 140ms ease, background 140ms ease;

  &:hover {
    border-color: rgba(141, 176, 255, 0.7);
    background: rgba(89, 130, 255, 0.13);
    transform: translateY(-1px);
  }

  @media (max-width: 640px) {
    padding: 0.58rem 0.62rem;
    border-radius: 10px;
  }
`;

const HealthHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
`;

const HealthLabel = styled.span`
  font-size: 0.82rem;
  color: ${plannerColors.text};
  font-weight: 600;

  @media (max-width: 640px) {
    font-size: 0.76rem;
  }
`;

const HealthValue = styled.span`
  font-size: 0.82rem;
  color: #8db0ff;
  font-weight: 700;

  @media (max-width: 640px) {
    font-size: 0.76rem;
  }
`;

const HealthTrack = styled.div`
  margin-top: 0.5rem;
  height: 8px;
  border-radius: 999px;
  background: rgba(89, 130, 255, 0.22);
  overflow: hidden;
`;

const HealthFill = styled.div<{ $value: number }>`
  height: 100%;
  border-radius: 999px;
  width: ${({ $value }) => `${$value}%`};
  background: linear-gradient(90deg, #7fa3ff 0%, #a5c0ff 100%);
`;

const SliderControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.58rem;

  @media (max-width: 640px) {
    margin-bottom: 0.45rem;
  }
`;

const SliderControlGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.34rem;
`;

const SliderButton = styled.button`
  border: 1px solid rgba(238, 242, 255, 0.28);
  border-radius: 999px;
  background: rgba(238, 242, 255, 0.09);
  color: #eef2ff;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.25rem 0.52rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(238, 242, 255, 0.16);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const SliderPill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.2rem 0.5rem;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #eef2ff;
  background: rgba(238, 242, 255, 0.12);
`;

const SliderTrack = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SliderSlide = styled.div`
  min-width: 100%;
  scroll-snap-align: start;
`;

function buildPlaceholderImage(label: string, startColor: string, endColor: string) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 360'>
    <defs>
      <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='${startColor}'/>
        <stop offset='100%' stop-color='${endColor}'/>
      </linearGradient>
    </defs>
    <rect width='640' height='360' fill='url(#g)'/>
    <circle cx='540' cy='60' r='74' fill='rgba(255,255,255,0.15)'/>
    <circle cx='90' cy='320' r='90' fill='rgba(255,255,255,0.09)'/>
    <text x='38' y='304' fill='rgba(255,255,255,0.93)' font-family='Segoe UI, Arial' font-size='36' font-weight='700'>${label}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

const UPCOMING_SERVICE_FALLBACK_IMAGE =
  'https://dqggtsiuqzwwjvugmovp.supabase.co/storage/v1/object/public/post-images/website-hero/1771454197430_kju2mxes1f.jpg';
const MY_ASSIGNMENT_FALLBACK_IMAGE = buildPlaceholderImage('My Assignment', '#4f8b7e', '#294e47');

function getUpcomingServiceImage(plan?: PlannerServicePlan) {
  if (!plan) return UPCOMING_SERVICE_FALLBACK_IMAGE;
  const media = (plan.service_plan_media_items || []).find((item) =>
    (item.media_type || '').toLowerCase().startsWith('image') && Boolean(item.file_url)
  );
  return media?.file_url || UPCOMING_SERVICE_FALLBACK_IMAGE;
}

function getAssignmentImage(avatar?: string | null) {
  return avatar || MY_ASSIGNMENT_FALLBACK_IMAGE;
}

function formatDateTime(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function estimateServiceDurationMinutes(plan: PlannerServicePlan): number {
  const itemMinutes = (plan.service_plan_items || []).reduce((total, item) => {
    const nextMinutes =
      typeof item.length_minutes === 'number' && Number.isFinite(item.length_minutes)
        ? item.length_minutes
        : 0;
    return total + nextMinutes;
  }, 0);

  return itemMinutes > 0 ? itemMinutes : 120;
}

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

export default function ChurchPlannerHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setDashboardServiceId } = useChurchPlannerRail();
  const planner = useChurchPlanner(true, user?.id);
  const { data: userProfile } = useCurrentUserProfile(user?.id);
  const [updatingAssignmentId, setUpdatingAssignmentId] = useState<string | null>(null);
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  const rolesQuery = usePlannerRoles(true);
  const roles = userProfile?.roles ?? user?.user_metadata?.roles;
  const canCreatePlans = hasAnyPermission(roles, [Permission.CREATE_CHURCH_PLAN]);
  const upcomingDay = planner.upcomingPlanDays[0] ?? null;
  const dayPlans = upcomingDay?.plans ?? [];
  const hasDaySlider = dayPlans.length > 1;
  const clampedActiveServiceIndex = dayPlans.length
    ? Math.min(activeServiceIndex, dayPlans.length - 1)
    : 0;
  const selectedPlan = dayPlans[clampedActiveServiceIndex] ?? planner.nextPlan ?? null;
  const currentPlan = useMemo(() => {
    const nowMs = Date.now();

    const inProgressPlans = planner.plans
      .filter((plan) => {
        const startMs = new Date(plan.service_date).getTime();
        if (Number.isNaN(startMs)) return false;

        const endMs = startMs + estimateServiceDurationMinutes(plan) * 60 * 1000;
        return startMs <= nowMs && nowMs < endMs;
      })
      .sort((a, b) => new Date(b.service_date).getTime() - new Date(a.service_date).getTime());

    return inProgressPlans[0] ?? null;
  }, [planner.plans]);
  const sliderPlans = dayPlans.length ? dayPlans : selectedPlan ? [selectedPlan] : currentPlan ? [currentPlan] : [];
  const activeDashboardPlan = sliderPlans[clampedActiveServiceIndex] ?? sliderPlans[0] ?? null;
  const dashboardSliderRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollResetRef = useRef<number | null>(null);

  useEffect(() => {
    if (!dayPlans.length) {
      setActiveServiceIndex(0);
      return;
    }
    setActiveServiceIndex((current) => Math.min(current, dayPlans.length - 1));
  }, [dayPlans.length]);

  useEffect(() => {
    setDashboardServiceId(activeDashboardPlan?.id || undefined);
    return () => {
      setDashboardServiceId(undefined);
    };
  }, [activeDashboardPlan?.id, setDashboardServiceId]);

  useEffect(() => {
    const element = dashboardSliderRef.current;
    if (!element) return;
    if (programmaticScrollResetRef.current) {
      window.clearTimeout(programmaticScrollResetRef.current);
    }
    isProgrammaticScrollRef.current = true;
    const nextLeft = clampedActiveServiceIndex * element.clientWidth;
    element.scrollTo({ left: nextLeft, behavior: 'smooth' });
    programmaticScrollResetRef.current = window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      programmaticScrollResetRef.current = null;
    }, 300);
  }, [clampedActiveServiceIndex]);

  useEffect(
    () => () => {
      if (programmaticScrollResetRef.current) {
        window.clearTimeout(programmaticScrollResetRef.current);
      }
    },
    []
  );

  const handlePrevService = () => {
    setActiveServiceIndex((current) => Math.max(0, current - 1));
  };

  const handleNextService = () => {
    setActiveServiceIndex((current) => Math.min(dayPlans.length - 1, current + 1));
  };

  const handleTrackScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasDaySlider) return;
    if (isProgrammaticScrollRef.current) return;
    const { scrollLeft, clientWidth } = event.currentTarget;
    if (!clientWidth) return;
    const nextIndex = Math.round(scrollLeft / clientWidth);
    const bounded = Math.min(Math.max(nextIndex, 0), dayPlans.length - 1);
    if (bounded !== activeServiceIndex) {
      setActiveServiceIndex(bounded);
    }
  };

  const openServiceFromCard = (planId: string) => {
    navigate(`/church-planner/services/${planId}`);
  };

  const handleServiceCardKeyDown = (event: React.KeyboardEvent<HTMLElement>, planId: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openServiceFromCard(planId);
  };

  const roleById = useMemo(() => {
    const map = new Map<string, { id: string; name: string; area?: string | null }>();
    (rolesQuery.data ?? []).forEach((role) => {
      map.set(role.id, role);
    });
    return map;
  }, [rolesQuery.data]);

  const currentUserAvailabilityByPlanId = useMemo(() => {
    const map = new Map<string, string>();
    if (!user?.id) return map;

    planner.availability.forEach((entry) => {
      if (entry.user_id !== user.id || !entry.service_plan_id) return;
      map.set(entry.service_plan_id, normalizePlannerStatus(entry.availability_status));
    });

    return map;
  }, [planner.availability, user?.id]);

  const requiredRowsByTeam = useMemo(() => {
    const byTeam = new Map<string, Array<{ id: string; team_id: string; role_id: string }>>();
    (planner.teamRequiredRolesQuery.data ?? []).forEach((row) => {
      const entries = byTeam.get(row.team_id) ?? [];
      entries.push(row);
      byTeam.set(row.team_id, entries);
    });
    return byTeam;
  }, [planner.teamRequiredRolesQuery.data]);

  const handleSetAssignmentStatus = async (
    entry: { assignment: { id?: string; status?: string | null }; plan: { id: string } },
    status: 'confirmed' | 'declined'
  ) => {
    if (!user?.id) {
      window.alert('Please sign in before setting availability.');
      return;
    }

    const assignmentId = entry.assignment.id || null;
    const currentStatus = normalizePlannerStatus(entry.assignment.status);
    const relatedAssignments = planner.plans
      .find((plan) => plan.id === entry.plan.id)?.service_plan_assignments
      ?.filter((assignment) => assignment.user_id === user.id && assignment.id) ?? [];
    setUpdatingAssignmentId(assignmentId || `availability-${entry.plan.id}`);
    try {
      await planner.upsertAvailability({
        service_plan_id: entry.plan.id,
        user_id: user.id,
        availability_status: status === 'declined' ? 'unavailable' : 'available',
        notes: null,
        updated_by: user.id,
      });

      if (status === 'declined') {
        const assignmentsToDecline = relatedAssignments.filter(
          (assignment) => normalizePlannerStatus(assignment.status) !== 'unavailable'
        );
        if (assignmentsToDecline.length) {
          await Promise.all(assignmentsToDecline.map((assignment) => planner.updateAssignment({
            assignmentId: assignment.id,
            updates: { status: 'declined' },
          })));
        } else if (currentStatus !== 'unavailable' && assignmentId) {
          await planner.updateAssignment({
            assignmentId,
            updates: { status: 'declined' },
          });
        }
      }

      if (status === 'confirmed') {
        const assignmentsToConfirm = relatedAssignments.filter((assignment) => {
          const normalizedStatus = normalizePlannerStatus(assignment.status);
          return normalizedStatus !== 'confirmed';
        });
        if (assignmentsToConfirm.length) {
          await Promise.all(assignmentsToConfirm.map((assignment) => planner.updateAssignment({
            assignmentId: assignment.id,
            updates: { status: 'confirmed' },
          })));
        } else if (currentStatus !== 'confirmed' && assignmentId) {
          await planner.updateAssignment({
            assignmentId,
            updates: { status: 'confirmed' },
          });
        }
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to update assignment status.');
    } finally {
      setUpdatingAssignmentId(null);
    }
  };

  if (planner.isLoading || rolesQuery.isLoading) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerSubtle>Loading service plans…</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (planner.isError || rolesQuery.isError) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerError>Unable to load planner data right now.</PlannerError>
        </PlannerCard>
      </PlannerPage>
    );
  }

  return (
    <PlannerPage>
      {hasDaySlider ? (
        <SliderControlRow>
          <SliderPill>
            {clampedActiveServiceIndex + 1}/{dayPlans.length}
          </SliderPill>
          <SliderControlGroup>
            <SliderButton
              type="button"
              onClick={handlePrevService}
              disabled={clampedActiveServiceIndex <= 0}
              aria-label="Show previous same-day service"
            >
              Prev
            </SliderButton>
            <SliderButton
              type="button"
              onClick={handleNextService}
              disabled={clampedActiveServiceIndex >= dayPlans.length - 1}
              aria-label="Show next same-day service"
            >
              Next
            </SliderButton>
          </SliderControlGroup>
        </SliderControlRow>
      ) : null}
      <SliderTrack ref={dashboardSliderRef} onScroll={handleTrackScroll}>
        {sliderPlans.map((plan, index) => {
          const assignmentsForPlan = planner.myAssignments.filter((entry) => entry.plan.id === plan?.id);
          const explicitAvailability = plan?.id ? currentUserAvailabilityByPlanId.get(plan.id) : null;
          const assignmentRows = assignmentsForPlan.length > 0
            ? assignmentsForPlan.map((entry) => ({ ...entry, availabilityOnly: false as const }))
            : plan && explicitAvailability === 'unavailable'
              ? [
                  {
                    assignment: {
                      id: `availability-${plan.id}`,
                      status: explicitAvailability,
                      service_plan_roles: null,
                      assigned_user: null,
                    },
                    plan,
                    availabilityOnly: true as const,
                  },
                ]
              : [];
          const filledByTeam = new Map<string, Set<string>>();
          if (plan) {
            (plan.service_plan_assignments ?? []).forEach((assignment) => {
              if (!assignment.user_id) return;
              const normalizedStatus = normalizePlannerStatus(assignment.status);
              if (normalizedStatus === 'unavailable') return;
              const role = firstRelation(assignment.service_plan_roles);
              const roleId = assignment.role_id || role?.id;
              if (!roleId) return;
              const resolvedRole = roleById.get(roleId) ?? role;
              const teamId = resolveTeamIdFromRole(resolvedRole);
              if (!teamId) return;
              const current = filledByTeam.get(teamId) ?? new Set<string>();
              current.add(roleId);
              filledByTeam.set(teamId, current);
            });
          }

          const healthForPlan = PLANNER_TEAM_DEFINITIONS.map((team) => {
            const requiredRows = requiredRowsByTeam.get(team.id) ?? [];
            const requiredRoleIds = Array.from(new Set(requiredRows.map((row) => row.role_id)));
            const filledSet = filledByTeam.get(team.id) ?? new Set<string>();
            const fulfilledRequired = requiredRoleIds.filter((roleId) => filledSet.has(roleId)).length;
            const totalRequired = requiredRoleIds.length;

            return {
              roleId: team.id,
              label: team.label,
              totalAssignments: totalRequired,
              filledAssignments: fulfilledRequired,
              coverage: totalRequired > 0 ? Math.round((fulfilledRequired / totalRequired) * 100) : 0,
              hasRequiredRoles: totalRequired > 0,
            };
          }).sort((a, b) => a.coverage - b.coverage);

          const totals = healthForPlan.reduce(
            (acc, metric) => {
              acc.total += metric.totalAssignments;
              acc.filled += metric.filledAssignments;
              return acc;
            },
            { total: 0, filled: 0 }
          );
          const overallForPlan = totals.total ? Math.round((totals.filled / totals.total) * 100) : 0;

          return (
            <SliderSlide key={plan?.id || `dashboard-empty-${index}`}>
              <ConnectedStack>
                {currentPlan ? (
                  <CurrentServiceCard
                    role="link"
                    tabIndex={0}
                    onClick={() => openServiceFromCard(currentPlan.id)}
                    onKeyDown={(event) => handleServiceCardKeyDown(event, currentPlan.id)}
                  >
                    <HeroLayout>
                      <HeroImageWrap>
                        <HeroImage
                          src={getUpcomingServiceImage(currentPlan)}
                          alt="Current service"
                          loading="lazy"
                        />
                      </HeroImageWrap>
                      <div>
                        <Row>
                          <HeroPill>Current Service</HeroPill>
                          <AccentLink to={`/church-planner/services/${currentPlan.id}`}>
                            Open Service
                          </AccentLink>
                        </Row>
                        <HeroTitle>{currentPlan.title}</HeroTitle>
                        <HeroMeta>{formatDateTime(currentPlan.service_date)}</HeroMeta>
                        <HeroMeta>{currentPlan.location || 'Main Sanctuary'}</HeroMeta>
                      </div>
                    </HeroLayout>
                  </CurrentServiceCard>
                ) : null}

                {plan && plan.id !== currentPlan?.id ? (
                  <HeroCard
                    role="link"
                    tabIndex={0}
                    onClick={() => openServiceFromCard(plan.id)}
                    onKeyDown={(event) => handleServiceCardKeyDown(event, plan.id)}
                  >
                    <HeroLayout>
                      <HeroImageWrap>
                        <HeroImage
                          src={getUpcomingServiceImage(plan)}
                          alt="Upcoming service"
                          loading="lazy"
                        />
                      </HeroImageWrap>
                      <div>
                        <Row>
                          <HeroPill>Upcoming Service</HeroPill>
                          <AccentLink to={`/church-planner/services/${plan.id}`}>
                            Open Service
                          </AccentLink>
                        </Row>
                        <HeroTitle>{plan.title}</HeroTitle>
                        <HeroMeta>{formatDateTime(plan.service_date)}</HeroMeta>
                        <HeroMeta>{plan.location || 'Main Sanctuary'}</HeroMeta>
                      </div>
                    </HeroLayout>
                  </HeroCard>
                ) : null}

                <MyAssignmentsCard>
                  <Row>
                    <SectionTitle>My Assignments</SectionTitle>
                    <AccentLink to="/church-planner/my-assignments">View all</AccentLink>
                  </Row>
                  {assignmentRows.length > 0 ? (
                    <AssignmentList>
                      {assignmentRows.slice(0, 5).map((entry) => (
                        <AssignmentItem key={entry.assignment.id}>
                          <AssignmentImage
                            src={getAssignmentImage(
                              entry.availabilityOnly
                                ? userProfile?.avatar
                                : Array.isArray(entry.assignment.assigned_user)
                                  ? entry.assignment.assigned_user[0]?.avatar
                                  : entry.assignment.assigned_user?.avatar
                            )}
                            alt="Assignment"
                            loading="lazy"
                          />
                          {(() => {
                            const normalizedStatus = normalizePlannerStatus(entry.assignment.status);
                            const isConfirmed = normalizedStatus === 'confirmed';
                            const isUnavailable = normalizedStatus === 'unavailable';
                            const role = Array.isArray(entry.assignment.service_plan_roles)
                              ? entry.assignment.service_plan_roles[0]
                              : entry.assignment.service_plan_roles;
                            return (
                              <AssignmentBody>
                                <AssignmentDate>{formatDateTime(entry.plan.service_date)}</AssignmentDate>
                                <AssignmentRolePill>{role?.name || (entry.availabilityOnly ? 'Unavailable for this service' : 'Unassigned role')}</AssignmentRolePill>
                                <AssignmentStatusPill>
                                  {formatPlannerStatusLabel(normalizedStatus)}
                                </AssignmentStatusPill>
                                <AssignmentActions>
                                  <AssignmentActionButton
                                    $tone="ok"
                                    $active={!isUnavailable && normalizedStatus === 'confirmed'}
                                    onClick={() => void handleSetAssignmentStatus(entry, 'confirmed')}
                                    disabled={updatingAssignmentId === entry.assignment.id}
                                  >
                                    {updatingAssignmentId === entry.assignment.id
                                      ? 'Saving...'
                                      : isUnavailable
                                        ? 'MARK AVAILABLE'
                                        : isConfirmed
                                          ? 'CONFIRMED'
                                          : 'Confirm'}
                                  </AssignmentActionButton>
                                  {!isConfirmed ? (
                                    <AssignmentActionButton
                                      $tone="bad"
                                      $active={isUnavailable}
                                      onClick={() => void handleSetAssignmentStatus(entry, 'declined')}
                                      disabled={updatingAssignmentId === entry.assignment.id}
                                    >
                                      MARK NOT AVAILABLE
                                    </AssignmentActionButton>
                                  ) : null}
                                </AssignmentActions>
                              </AssignmentBody>
                            );
                          })()}
                        </AssignmentItem>
                      ))}
                    </AssignmentList>
                  ) : (
                    <PlannerSubtle style={{ marginTop: '0.65rem' }}>
                      {plan
                        ? 'You do not have assignments for this service yet.'
                        : 'You do not have upcoming assignments yet.'}
                    </PlannerSubtle>
                  )}
                </MyAssignmentsCard>

                <ServiceHealthCard>
                  <Row>
                    <SectionTitle>Service Health</SectionTitle>
                    <AccentLink to="/church-planner/service-health">Details</AccentLink>
                  </Row>
                  <HealthGrid style={{ marginTop: '0.65rem' }}>
                    {healthForPlan.map((item) => (
                      plan?.id && item.hasRequiredRoles ? (
                        <HealthItemLink
                          key={item.roleId}
                          to={`/church-planner/services/${plan.id}/health/${item.roleId}`}
                          aria-label={`Open ${item.label} health details`}
                        >
                          <HealthHead>
                            <HealthLabel>{item.label}</HealthLabel>
                            <HealthValue>{`${item.coverage}%`}</HealthValue>
                          </HealthHead>
                          <HealthTrack>
                            <HealthFill $value={item.coverage} />
                          </HealthTrack>
                        </HealthItemLink>
                      ) : (
                        <HealthItem key={item.roleId}>
                          <HealthHead>
                            <HealthLabel>{item.label}</HealthLabel>
                            <HealthValue>{item.hasRequiredRoles ? `${item.coverage}%` : 'N/A'}</HealthValue>
                          </HealthHead>
                          <HealthTrack>
                            <HealthFill $value={item.coverage} />
                          </HealthTrack>
                        </HealthItem>
                      )
                    ))}
                    {healthForPlan.every((item) => !item.hasRequiredRoles) ? (
                      <PlannerSubtle style={{ marginTop: 0 }}>
                        Team health updates after required roles are set and assigned members are available.
                      </PlannerSubtle>
                    ) : null}
                  </HealthGrid>
                  {healthForPlan.length > 0 ? (
                    <PlannerSubtle style={{ marginTop: '0.7rem' }}>
                      Overall assigned/available coverage: {overallForPlan}%
                    </PlannerSubtle>
                  ) : null}
                </ServiceHealthCard>
              </ConnectedStack>
            </SliderSlide>
          );
        })}
      </SliderTrack>
      {!sliderPlans.length && canCreatePlans ? (
        <EmptyUpcomingActions>
          <AddServiceLink to="/church-planner/services/new">Add New Service</AddServiceLink>
        </EmptyUpcomingActions>
      ) : null}
    </PlannerPage>
  );
}
